import { useState, type KeyboardEvent } from "react";

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  ariaLabel?: string;
  placeholder?: string;
  invalid?: boolean;
  max?: number;
};

export function ChipInput({
  value,
  onChange,
  ariaLabel,
  placeholder = "Add and press Enter…",
  invalid,
  max = 30,
}: Props) {
  const [draft, setDraft] = useState("");

  function commit(raw: string) {
    const trimmed = raw.trim();
    if (!trimmed) return;
    if (value.includes(trimmed)) return;
    if (value.length >= max) return;
    onChange([...value, trimmed]);
    setDraft("");
  }

  function removeAt(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && !draft && value.length > 0) {
      removeAt(value.length - 1);
    }
  }

  return (
    <div
      className={`flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-md border bg-surface px-2.5 py-2 transition-colors ${
        invalid
          ? "border-danger bg-danger-soft"
          : "border-border focus-within:border-primary focus-within:shadow-[0_0_0_3px_var(--primary-soft)]"
      }`}
    >
      {value.map((chip, i) => (
        <span
          key={`${chip}-${i}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-2.5 py-0.5 text-xs font-medium text-primary-hover"
        >
          {chip}
          <button
            type="button"
            onClick={() => removeAt(i)}
            aria-label={`Remove ${chip}`}
            className="cursor-pointer text-text-muted opacity-60 hover:opacity-100"
          >
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        aria-label={ariaLabel}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit(draft)}
        placeholder={value.length === 0 ? placeholder : ""}
        className="min-w-[80px] flex-1 border-none bg-transparent text-sm text-text outline-none placeholder:text-text-soft"
      />
    </div>
  );
}
